import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { ApiError, apiJson } from '../lib/api'
import { type Gate, STOCK_PREVIEW, canOpen, landingPath, useAccess } from '../lib/access'
import styles from './AccessGate.module.css'

// A screen the caller's permission set does not open (D1.7). The rail never
// offers it, but a bookmark or a pasted link still lands here. The screen is
// not drawn, so it never shows an empty table that reads as "nothing here";
// instead the server is asked, with one read it guards the same way, and its
// refusal is what the caller sees. If the server lets the read through, the
// access this browser held was out of date: it is asked for again and the
// screen is drawn.
//
// Who can change the answer depends on the refusal. A permission or a grant
// is an ADMIN's to give; the product and the Stock & Dispatch licence are
// Dataphyll's (D2, approved 2026-10-01), so those refusals do not send the
// caller to an ADMIN, and one who may see the module preview is offered it.
const SUBSCRIPTION_CODES = new Set(['MODULE_NOT_LICENSED', 'PRODUCT_NOT_AVAILABLE'])

export function AccessGate({ gate, probe, children }: { gate: Gate; probe: string; children: React.ReactNode }) {
  const access = useAccess()
  if (canOpen(access, gate)) return <>{children}</>
  return <Refused probe={probe}>{children}</Refused>
}

function Refused({ probe, children }: { probe: string; children: React.ReactNode }) {
  const { user } = useAuth()
  const access = useAccess()
  const { refreshAccess } = useWorkspace()
  const [state, setState] = useState<{ kind: 'asking' } | { kind: 'refused'; message: string; code: string } | { kind: 'allowed' }>({ kind: 'asking' })

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'asking' })
    apiJson(user, probe)
      .then(() => !cancelled && setState({ kind: 'allowed' }))
      .catch((err) => {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 403) setState({ kind: 'refused', message: err.message, code: String(err.body?.code || '') })
        // Anything but a refusal (a bad query, a fault) means the guard let
        // the read through: the screen shows its own answer.
        else setState({ kind: 'allowed' })
      })
    return () => {
      cancelled = true
    }
  }, [user, probe])

  useEffect(() => {
    if (state.kind === 'allowed') refreshAccess()
  }, [state.kind, refreshAccess])

  if (state.kind === 'allowed') return <>{children}</>
  if (state.kind === 'asking') return <div className={styles.page} aria-busy="true" />
  const home = landingPath(access)
  const subscription = SUBSCRIPTION_CODES.has(state.code)
  const preview = state.code === 'MODULE_NOT_LICENSED' && canOpen(access, STOCK_PREVIEW)
  return (
    <div className={styles.page}>
      <div className={styles.card} role="alert">
        <h1 className={styles.title}>Access denied</h1>
        <p className={styles.message}>{state.message}</p>
        <p className={styles.hint}>
          {subscription ? 'What this workspace has bought is set by Dataphyll, not in this workspace. ' : 'Your access is set by an ADMIN in this workspace. '}
          {preview && (
            <>
              <Link to="/stock-dispatch/preview">See what Stock & Dispatch does</Link>
              {' · '}
            </>
          )}
          <Link to={home}>Go to a screen you can open</Link>
        </p>
      </div>
    </div>
  )
}
